import { serverTimestamp } from 'firebase/firestore';
import { isEmpty } from 'ramda';
import { ofType } from 'redux-observable';
import { type Observable, from, of } from 'rxjs';
import {
  auditTime,
  catchError,
  filter,
  ignoreElements,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Weddings } from '@bridebook/models';
import { Events } from '@bridebook/models/source/models/Weddings/Events';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { env } from '@bridebook/web/lib/env';
import { createMainEvent } from '@bridebook/web/lib/events/utils';
import { ShortlistActionTypes } from '@bridebook/web/lib/shortlist/action-types';
import { getWeddingVenue } from '@bridebook/web/lib/shortlist/selectors';
import { inheritActionMeta } from '@bridebook/web/lib/epics/inherit-action-meta';
import {
  PLANNER_ANALYTICS_SOURCE,
  isAgentAction,
} from '@bridebook/web/lib/ai-planner/client-actions/agent-dispatch';
import type { Action, IApplicationState, IDeps, IEpic, IEpicDeps } from '@bridebook/web/lib/types';
import { noopAction } from '@bridebook/web/lib/utils';
import { appError } from '../../app/actions';
import {
  type IChangeCurrencyAction,
  type IFetchCountryBoundsStartAction,
  type IUpdateWeddingFieldAction,
  type IUpdateWeddingFieldSuccessAction,
  type IUpdateWeddingPreferencesAction,
  type IWeddingProfileSaveDateAction,
  WeddingActionTypes,
} from '../action-types';
import { initialiseChecklist, reInitialiseChecklist } from '../../checklist/actions';
import {
  fetchCountryBoundsSuccess,
  pbStatusAnalytics,
  setWeddingDate,
  updateWeddingField,
} from '../actions';
import { getVenueBooked, getWeddingProfileId } from '../selectors';
import type { ILatLongBounds } from '../types';
import isDateExact from '../utils/is-date-exact';

const saveDate = async (getState: IDeps['getState']) => {
  const {
    datepicker: { datePickerDate },
  } = getState();

  return { datePickerDate };
};

export const weddingProfileSaveDateEpics = (
  action$: Observable<IWeddingProfileSaveDateAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.SAVE_WEDDING_DATE),
    auditTime(300),
    withLatestFrom(state$),
    mergeMap(([, state]) =>
      from(saveDate(() => state)).pipe(
        map(() => ({
          type: WeddingActionTypes.SAVE_WEDDING_DATE_SUCCESS,
          // for analytics
          payload: { name: 'weddingDate' },
        })),
        catchError((error) =>
          of(appError({ error, feature: 'Wedding' }), {
            type: WeddingActionTypes.SAVE_WEDDING_DATE_ERROR,
            payload: error,
          }),
        ),
      ),
    ),
  );

/**
 * Function `updateBookingsVenueEpic`
 * Listens to shortlist actions in order to
 * update bookings.venue (previously 'venueBooked')
 *
 * @function updateBookingsVenueEpic
 * @param {action$, IDeps}
 */
export const updateBookingsVenueEpic: IEpic = (action$, { state$ }) =>
  action$.pipe(
    ofType(
      ShortlistActionTypes.ON_SUPPLIERS_SHORTLISTED_SUCCESS,
      ShortlistActionTypes.ON_REMOVED_SUPPLIERS_FROM_SHORTLIST,
    ),
    auditTime(250),
    withLatestFrom(state$),
    mergeMap(([, state]: [Action, IApplicationState]) => {
      const {
        app: { pathname },
      } = state;

      //We want to check if the shortlist is loaded to prevent changing the bookings value
      //when ON_REMOVED_SUPPLIERS_FROM_SHORTLIST or ON_SUPPLIERS_SHORTLISTED_SUCCESS
      //is emitted first and the state is not ready yet
      if (!state.shortlist.loaded) {
        return of(noopAction());
      }

      const weddingVenue = getWeddingVenue(state);
      const venueBooked = getVenueBooked(state);
      const isOnboarding = pathname.startsWith('/onboarding/venue');
      const actions: Action[] = [];
      if ((weddingVenue && !venueBooked) || isOnboarding) {
        actions.push(updateWeddingField('bookings', { bookings: { venue: true } }));
        actions.push(pbStatusAnalytics(false));
      } else if (!weddingVenue && venueBooked) {
        actions.push(updateWeddingField('bookings', { bookings: { venue: false } }));
        actions.push(pbStatusAnalytics(true));
      }

      return of(...actions);
    }),
  );

/**
 * Applies a wedding-date update sent as a plain `{ type, payload }` action
 * (the AI planner dispatches it raw): persists the date through the standard
 * wedding-field write and rebuilds the checklist the same way the datepicker
 * save flow does — checklist tasks are date-relative, so replacing an
 * existing date re-initialises them.
 */
export const setWeddingDateEpic = (
  action$: Observable<ReturnType<typeof setWeddingDate>>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.SET_WEDDING_DATE),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { date } = trigger.payload;
      const previousDate = state.weddings.profile?.date;
      const actions: Action[] = [updateWeddingField('date', { date })];
      if (previousDate && !isEmpty(previousDate)) {
        if (state.checklist.initialised) actions.push(reInitialiseChecklist());
        actions.push(initialiseChecklist(false));
      }
      // The declared outcome of this intent is the field write's own success,
      // one epic further — the meta must ride the intermediate action there.
      return of(...actions).pipe(inheritActionMeta(trigger));
    }),
  );

export const updateWeddingFieldEpic = (
  action$: Observable<IUpdateWeddingFieldAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.UPDATE_WEDDING_FIELD),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { name, value, extraAnalytics, ignoreAnalytics } = trigger.payload;
      const {
        weddings: { profile },
      } = state;

      if (!profile?.id) {
        return of();
      }

      return from(
        Weddings._.getById(profile.id).set(value, name === 'location' ? [name] : true),
      ).pipe(
        mergeMap(() =>
          of({
            type: WeddingActionTypes.UPDATE_WEDDING_FIELD_SUCCESS,
            payload: { name, value, extraAnalytics, ignoreAnalytics },
          }),
        ),
        catchError((error) =>
          of(appError({ error, feature: 'Wedding' }), {
            type: WeddingActionTypes.UPDATE_WEDDING_FIELD_ERROR,
            payload: { name, value, error },
          }),
        ),
        inheritActionMeta(trigger),
      );
    }),
  );

/**
 * Persists a currency change. The code is validated against the currencies
 * Bridebook actually supports (the same set the settings dropdown offers) —
 * schema-level checks only cover the ISO shape. Existing budget amounts are
 * not converted; only the display currency changes.
 */
export const changeCurrencyEpic = (
  action$: Observable<IChangeCurrencyAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.UPDATE_CURRENCY),
    withLatestFrom(state$),
    mergeMap(([trigger, state]) => {
      const { value } = trigger.payload;
      const supported = new Set(
        gazetteer.getCountries({ blocked: false }).map((country) => country.currency),
      );
      if (!supported.has(value)) {
        return of({
          type: WeddingActionTypes.UPDATE_CURRENCY_ERROR,
          payload: {
            value,
            error: new Error(`"${value}" is not a currency Bridebook supports.`),
          },
        }).pipe(inheritActionMeta(trigger));
      }

      const {
        weddings: { profile },
      } = state;
      if (!profile?.id) {
        return of();
      }

      return from(Weddings._.getById(profile.id).set({ l10n: { currency: value } })).pipe(
        mergeMap(() =>
          of(
            {
              type: WeddingActionTypes.UPDATE_CURRENCY_SUCCESS,
              payload: { value },
            },
            {
              type: 'EDITED_WEDDING_DETAILS_ANALYTICS',
              payload: {
                name: 'currency',
                ...(isAgentAction(trigger) && { weddingDetailsLocation: PLANNER_ANALYTICS_SOURCE }),
              },
            },
          ),
        ),
        catchError((error: Error) =>
          of(appError({ error, feature: 'Wedding' }), {
            type: WeddingActionTypes.UPDATE_CURRENCY_ERROR,
            payload: { value, error },
          }),
        ),
        inheritActionMeta(trigger),
      );
    }),
  );

export const fetchCountryBoundsEpic = (action$: Observable<IFetchCountryBoundsStartAction>) =>
  action$.pipe(
    ofType(WeddingActionTypes.FETCH_PROFILE_COUNTRY_BOUNDS_START),
    mergeMap(({ payload }) => {
      // Temporary exception to show autocomplete suggestions for Illinois
      // @see https://bridebook.atlassian.net/browse/LIVE-12910
      const countryCode = payload === CountryCodes.US ? 'US_Illinois' : payload;

      const promise = fetch(
        `${env.STATIC}/static/country-lat-long-bounds/${countryCode}.json`,
      ).then((data) => data.json() as Promise<ILatLongBounds>);

      return from(promise).pipe(
        map((countryLatLongBounds) => fetchCountryBoundsSuccess(countryLatLongBounds)),
        catchError((error) =>
          of(appError({ error, feature: 'Wedding' }), {
            type: WeddingActionTypes.FETCH_PROFILE_COUNTRY_BOUNDS_ERROR,
            payload: error,
          }),
        ),
      );
    }),
  );

export const updateWeddingPreferencesEpic = (
  action$: Observable<IUpdateWeddingPreferencesAction>,
  { state$ }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.UPDATE_WEDDING_PREFERENCES),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const getPromise = async () => {
        const { id: preferenceId, property, value } = payload;
        const weddingId = getWeddingProfileId(state);
        const wedding = Weddings._.getById(weddingId);
        const preferences = await wedding.Preferences.getById(preferenceId).get();
        if (preferences) {
          wedding.Preferences.getById(preferenceId).set({ [property]: value });
        } else {
          wedding.Preferences.getById(preferenceId).set({
            createdAt: serverTimestamp(),
            id: preferenceId,
            [property]: value,
          });
        }
      };

      return from(getPromise()).pipe(
        mergeMap(() => of()),
        catchError((error: Error) =>
          of(appError({ error, feature: 'Updating wedding preferences' })),
        ),
      );
    }),
  );

// Creates the Main Event if it doesn't exist (only if venue is set and date is exact)
// @deprecated TODO: Remove after LIVE-22764 RSVP Guest Management is released
export const createMainEventIfNotExistsEpic = (
  action$: Observable<IUpdateWeddingFieldSuccessAction>,
  { state$, queryClient }: IEpicDeps,
) =>
  action$.pipe(
    ofType(WeddingActionTypes.UPDATE_WEDDING_FIELD_SUCCESS),
    withLatestFrom(state$),
    filter(([action]) => action.payload.name === 'date'),
    tap(async ([action, state]) => {
      if (isDateExact(action.payload.value.date)) {
        const weddingId = state.weddings.profile?.id;
        const mainEventRef = await Weddings._.getById(weddingId).Events.getById(Events.MAIN_EVENT);
        const mainEvent = await mainEventRef.get();
        const weddingVenue = getWeddingVenue(state);
        if (!mainEvent && weddingVenue) {
          await createMainEvent({ weddingId, queryClient });
        }
      }
    }),
    ignoreElements(),
  );
